import { uid } from 'uid';
import { html, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { dispatchEvent } from '@fil-global/shared';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-close';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-tick';

import componentStyles from './fil-chip.styles.js';

/**
 * @tag fil-chip
 * @property {"primary"|"secondary"} [appearance='primary'] Appearance of the component
 * @property {string} [name=""] Name of the component
 * @property {"standard" | "small"} [size="standard"] Size of the component
 * @property {"input"|"singleselect"|"multiselect"} [variation='input'] Variation of the component
 * @property {boolean} [checked=false] HTML checked state of the component
 * @property {boolean} [disabled=false] HTML disabled state of the component
 * @property {boolean} [error=false] Error state of the component
 * @property {string} [label] Label of the component
 * @property {string} [name] Name of the component
 * @property {string} [value] Value of the component
 * @fires close.gds.chip (event: Event) => void
 * @fires change.gds.chip (event: Event) => void
 */
export class FilChip extends LitElement {
  static properties = {
    appearance: { type: String }, // 'primary' | 'secondary'
    checked: { type: Boolean },
    disabled: { type: Boolean },
    error: { type: Boolean },
    label: { type: String },
    name: { type: String },
    size: { type: String }, // large | standard | small | xsmall
    value: { type: String },
    variation: { type: String }, // input | singleselect | multiselect
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.appearance = 'primary';
    this.checked = false;
    this.disabled = false;
    this.error = false;
    this.size = 'standard';
    this.variation = 'input';

    this.uid = uid();
  }

  onChange() {
    if (this.variation === 'singleselect') {
      this.checked = true;
    } else if (this.variation === 'multiselect') {
      const input = this.shadowRoot.querySelector('input');
      this.checked = input.checked;
    }
  }

  onClick() {
    const { name, value } = this.renderRoot.querySelector('input');
    dispatchEvent(this, 'close.gds.chip', {
      name,
      value,
    });
  }

  onKeypress(event) {
    // check for key 'space'
    if (event.keyCode === 32 || event.keyCode === 13) {
      event.preventDefault();

      if (this.variation === 'input') {
        this.onClick();
      }

      if (!this.disabled) {
        if (this.variation === 'multiselect') {
          const input = event.target.parentNode.querySelector('input');
          input.checked = !input.checked;
        }
        this.onChange();
      }
    }
  }

  update(changedProperties) {
    super.update(changedProperties);

    // Event should be dispatched only inside live cycle method not in change handler.
    // State changes by user interaction will always trigger a change event where state changes
    // programmatically (by script) won't do. This is intended as you can see in the
    // documentation of Lit (https://lit.dev/docs/components/events/#when-to-dispatch-an-event).
    // Unfortunately we are in the need to dispatch the change event regardless if the state
    // change is done by user interaction or programmatically. We solve it by moving the dispatch
    // into the live cycle method as this one will be called in both cases.
    if (changedProperties.has('checked')) {
      if (this.variation === 'singleselect') {
        if (this.checked) {
          if (changedProperties.get('checked') !== undefined && !changedProperties.get('checked')) {
            this.onChange();
            const { value } = this.renderRoot.querySelector('input');
            dispatchEvent(this, 'change.singleselect.gds.chip', {
              value,
              variation: this.variation,
            });
          }
        }
      } else if (this.variation === 'multiselect') {
        if (changedProperties.get('checked') !== undefined) {
          this.onChange();
          const { checked, name } = this.renderRoot.querySelector('input');
          dispatchEvent(this, 'change.gds.chip', {
            checked,
            name,
            variation: this.variation,
          });
        }
      }
    }
  }

  renderLabel() {
    return html`${this.label && this.label.length > 20
      ? `${this.label.substring(0, 20)} ... ${this.label.slice(-4)}`
      : this.label}`;
  }

  render() {
    const classes = {
      [`fil-chip--${this.appearance}`]: this.appearance !== 'primary',
      [`fil-chip--disabled`]: this.disabled,
      [`fil-chip--error`]: this.error,
      [`fil-chip--${this.size}`]: this.size !== 'standard',
      [`fil-chip--${this.variation}`]: this.variation !== 'input',
      [`fil-chip--selected`]: this.variation !== 'input' ? this.checked : false,
    };

    return this.variation === 'input'
      ? html`<div
          aria-label="${this.label}"
          title="${this.label}"
          class="fil-chip${classMap(classes)}"
          role="option"
        >
          <label class="fil-chip__label" tabindex="0" @keypress=${this.onKeypress}
            ><input
              name=${ifDefined(this.name)}
              type="hidden"
              tabindex="-1"
              value=${ifDefined(this.value)} />${this.renderLabel()}
            <fil-icon-close
              class="fil-chip__close"
              @click=${this.onClick}
              size="12"
              role="button"
              label="Close"
              aria-label="Close"
            ></fil-icon-close
          ></label>
        </div>`
      : html`<div
          class="fil-chip${classMap(classes)}"
          role='option'
          aria-checked=${this.checked}
          @keypress=${this.onKeypress}
          title="${this.label}"
          aria-selected=${this.checked}
        >
          <label
            class="fil-chip__label"
            id="label_${this.uid}"
            tabindex="0"
            ><input
              aria-labelledby="label_${this.uid}"
              class="fil-chip__field"
              @change=${this.onChange}
              .checked=${this.checked}
              ?disabled=${this.disabled}
              name=${ifDefined(this.name)}
              tabindex="-1"
              type=${this.variation === 'multiselect' ? 'checkbox' : 'radio'}
              value=${ifDefined(this.value)}
            />${this.variation === 'multiselect' && this.checked
              ? html`<fil-icon-custom-tick
                  size=${this.size === 'xs' ? 8 : 12}
                ></fil-icon-custom-tick>`
              : null}
            ${this.renderLabel()}</label
          >
        </div>`;
  }
}

export default FilChip;
